import type { CustomForm } from '~/composables/customForms/types';
import {
  BLOCK_TEXT_TYPE,
  blockTextContent,
  effectiveDisplayOrder,
  formElementLabel,
  CHECKBOX_ON_VALUE,
  CHECKBOX_TYPE,
  isCheckboxField,
  isDisplayOnlyField,
  isSignatureField,
  SIGNATURE_TYPE,
} from '~/composables/customForms/types';
import type { FormFieldFormStep } from '~/composables/types/DynamicForm';
import type { PlacementCondition } from '~/composables/customForms/placementConditions';
import salvationArmyLogoUrl from '~/assets/images/salvation-army-logo.png';
// open-licensed fonts (SIL OFL / Apache 2.0, from Google Fonts) embedded in the PDF when a placement uses one
import carlitoFontUrl from '~/assets/fonts/pdf/Carlito-Regular.ttf?url';
import robotoFontUrl from '~/assets/fonts/pdf/Roboto-Regular.ttf?url';
import openSansFontUrl from '~/assets/fonts/pdf/OpenSans-Regular.ttf?url';
import latoFontUrl from '~/assets/fonts/pdf/Lato-Regular.ttf?url';
import montserratFontUrl from '~/assets/fonts/pdf/Montserrat-Regular.ttf?url';
import poppinsFontUrl from '~/assets/fonts/pdf/Poppins-Regular.ttf?url';
import sourceSans3FontUrl from '~/assets/fonts/pdf/SourceSans3-Regular.ttf?url';
import notoSansFontUrl from '~/assets/fonts/pdf/NotoSans-Regular.ttf?url';
import merriweatherFontUrl from '~/assets/fonts/pdf/Merriweather-Regular.ttf?url';
import playfairDisplayFontUrl from '~/assets/fonts/pdf/PlayfairDisplay-Regular.ttf?url';

/**
 * Fields that can be dropped onto a custom PDF template.
 * Presentation fields come from the form's questions; the rest are known by the system. "Application
 * Questions" fields (see `buildApplicationQuestionFields`) are grouped dynamically, one section per
 * intake step, so this isn't a fixed set of names.
 */

export type PdfFieldSource = string;
export const FIXED_MAPPER_SOURCES = [
  'Symbols',
  'Presentation',
  'Application',
  'System',
  'Utility',
  'Caseworker',
  'Signature',
];

export interface MapperField {
  key: string;
  label: string;
  source: PdfFieldSource;
  /**
   * 'signature' fields are drawn as the applicant's signature image (PNG / JPEG data URL) or,
   * when the value is plain text, in a script-like font
   */
  kind?: 'text' | 'signature' | 'symbol' | 'image';
  /** `kind: 'symbol'`: what is drawn — a check mark / X as lines, Y / N as bold letters */
  symbol?: PlacementSymbol;
  /** `kind: 'image'`: the (PNG) image drawn, scaled to fit the box */
  imageSrc?: string;
  /**
   * `kind: 'symbol'` drawn only when the field has a value (a Check Box question: the check mark prints
   * when the box was ticked); otherwise a symbol prints on every PDF
   */
  whenAnswered?: boolean;
  /** Paragraph-sized content (a Block Text): placed with a larger box by default */
  multiline?: boolean;
  /** Value used by "Preview with sample data" */
  sampleValue: string;
}

export type TextAlign = 'left' | 'center' | 'right' | 'justify';
export type PlacementSymbol = 'checkmark' | 'x' | 'Y' | 'N';

/**
 * "Font" of a placed field. The text fonts are the PDF standard fonts — built into every PDF reader, so
 * nothing is embedded — under the names people know them by (Helvetica is Arial's metric twin, Times is
 * Times New Roman, Courier is Courier New). Tahoma, Verdana, Calibri… are licensed Microsoft fonts that
 * can't be shipped with the app. The two barcodes print the value as Code 39 / Code 128 bars (see
 * `barcodes.ts`). Missing on a placement → Arial / Helvetica, the font fields always had.
 */
export type PlacementFont =
  | 'helvetica'
  | 'times'
  | 'courier'
  | 'carlito'
  | 'roboto'
  | 'openSans'
  | 'lato'
  | 'montserrat'
  | 'poppins'
  | 'sourceSans3'
  | 'notoSans'
  | 'merriweather'
  | 'playfairDisplay'
  | 'code39'
  | 'code128';
export interface PlacementFontOption {
  value: PlacementFont;
  label: string;
  /** `StandardFonts` key of pdf-lib (text fonts built into every PDF reader) */
  standardFont?: string;
  /** TrueType file of a font embedded in the PDF — only the characters used, unless `embedWhole` */
  fileUrl?: string;
  /**
   * Embed the whole font: pdf-lib's subsetting garbles a few fonts (Carlito and Noto Sans printed
   * scattered characters, checked by rendering each font both ways). Costs the font's size (~600 KB).
   */
  embedWhole?: boolean;
  /** Barcode drawn instead of text */
  barcode?: 'code39' | 'code128';
  /** How the mapper shows it on the placed box */
  css?: Record<string, string>;
}
/** Alphabetical order by label, case-insensitive and numeric-aware ("Code 39" before "Code 128") */
export const byLabel = (a: { label: string }, b: { label: string }) =>
  a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
const ARIAL = 'Arial, Helvetica, sans-serif';
const TIMES = '"Times New Roman", Times, serif';
const COURIER = '"Courier New", Courier, monospace';
/** Alphabetical, as the Font dropdown lists them (numeric-aware: Code 39 before Code 128) */
export const FONT_OPTIONS: PlacementFontOption[] = [
  { value: 'helvetica', label: 'Arial / Helvetica', standardFont: 'Helvetica', css: { fontFamily: ARIAL } },
  { value: 'times', label: 'Times New Roman', standardFont: 'TimesRoman', css: { fontFamily: TIMES } },
  { value: 'courier', label: 'Courier New', standardFont: 'Courier', css: { fontFamily: COURIER } },
  { value: 'carlito', label: 'Carlito (Calibri style)', fileUrl: carlitoFontUrl, embedWhole: true, css: { fontFamily: '"PDF Carlito", sans-serif' } },
  { value: 'roboto', label: 'Roboto', fileUrl: robotoFontUrl, css: { fontFamily: '"PDF Roboto", sans-serif' } },
  { value: 'openSans', label: 'Open Sans', fileUrl: openSansFontUrl, css: { fontFamily: '"PDF Open Sans", sans-serif' } },
  { value: 'lato', label: 'Lato', fileUrl: latoFontUrl, css: { fontFamily: '"PDF Lato", sans-serif' } },
  { value: 'montserrat', label: 'Montserrat', fileUrl: montserratFontUrl, css: { fontFamily: '"PDF Montserrat", sans-serif' } },
  { value: 'poppins', label: 'Poppins', fileUrl: poppinsFontUrl, css: { fontFamily: '"PDF Poppins", sans-serif' } },
  { value: 'sourceSans3', label: 'Source Sans 3', fileUrl: sourceSans3FontUrl, css: { fontFamily: '"PDF Source Sans 3", sans-serif' } },
  { value: 'notoSans', label: 'Noto Sans', fileUrl: notoSansFontUrl, embedWhole: true, css: { fontFamily: '"PDF Noto Sans", sans-serif' } },
  { value: 'merriweather', label: 'Merriweather', fileUrl: merriweatherFontUrl, css: { fontFamily: '"PDF Merriweather", serif' } },
  { value: 'playfairDisplay', label: 'Playfair Display', fileUrl: playfairDisplayFontUrl, css: { fontFamily: '"PDF Playfair Display", serif' } },
  { value: 'code39', label: 'Code 39 (barcode)', barcode: 'code39' },
  { value: 'code128', label: 'Code 128 (barcode)', barcode: 'code128' },
].sort(byLabel); // prettier-ignore
export const DEFAULT_PLACEMENT_FONT: PlacementFont = 'helvetica';
export const fontOptionOf = (font?: PlacementFont | null): PlacementFontOption =>
  FONT_OPTIONS.find((o) => o.value === font) ?? FONT_OPTIONS.find((o) => o.value === DEFAULT_PLACEMENT_FONT)!;

/**
 * Makes an embedded font available to the page, so the mapper's boxes show their label in it (the
 * `css` of the option names it "PDF {font}"). Loaded on first use, once.
 */
const previewFontsLoaded = new Set<string>();
export const ensurePreviewFont = (option: PlacementFontOption) => {
  const family = option.css?.fontFamily?.match(/^"([^"]+)"/)?.[1];
  if (!option.fileUrl || !family || previewFontsLoaded.has(family) || typeof FontFace === 'undefined') return;
  previewFontsLoaded.add(family);
  new FontFace(family, `url(${option.fileUrl})`)
    .load()
    .then((loaded) => document.fonts.add(loaded))
    .catch((e) => console.warn(`unable to load the font ${family}`, e));
};

/**
 * Symbols and images placed like any other field (with Conditions, e.g. a check mark printed on a
 * template's check box only for the applications that meet them). They print the same thing on every
 * PDF — nothing to fill — and have no font size / alignment: drawn centred, as large as the box allows.
 */
export const SYMBOL_MAPPER_FIELDS: MapperField[] = [
  { key: 'symbol:checkmark', label: 'Check Mark', source: 'Symbols', kind: 'symbol', symbol: 'checkmark', sampleValue: '✓' },
  { key: 'symbol:x', label: 'Graphical X', source: 'Symbols', kind: 'symbol', symbol: 'x', sampleValue: '✘' },
  { key: 'symbol:Y', label: 'Y', source: 'Symbols', kind: 'symbol', symbol: 'Y', sampleValue: 'Y' },
  { key: 'symbol:N', label: 'N', source: 'Symbols', kind: 'symbol', symbol: 'N', sampleValue: 'N' },
  {
    key: 'image:salvationArmyLogo',
    label: 'Salvation Army Logo',
    source: 'Symbols',
    kind: 'image',
    imageSrc: salvationArmyLogoUrl,
    sampleValue: '',
  },
];
/** Drawn as a fixed graphic: no font size / alignment to set */
export const isGraphicField = (field?: MapperField | null) =>
  field?.kind === 'signature' || field?.kind === 'symbol' || field?.kind === 'image';

/**
 * What a placement prints instead of the field's own value ("Display as"). The check mark and the X are
 * drawn as lines (the standard PDF fonts have no check glyph), Y / N as text. Printed whenever the
 * placement is drawn — even when the field itself has no value — so it's typically paired with Conditions.
 */
export type PlacementDisplay = 'checkmark' | 'x' | 'Y' | 'N';
export const PLACEMENT_DISPLAY_OPTIONS: { value: PlacementDisplay; label: string; symbol: string }[] = [
  { value: 'checkmark', label: 'Checkmark (✓)', symbol: '✓' },
  { value: 'x', label: 'Graphical X (✗)', symbol: '✗' },
  { value: 'Y', label: 'Capital Y', symbol: 'Y' },
  { value: 'N', label: 'Capital N', symbol: 'N' },
];

/**
 * A placed field on a PDF template. Coordinates are PDF points (1/72 in) measured from the
 * TOP-LEFT corner of the page, independent of zoom. pdf-lib's bottom-left origin is derived at
 * fill time as `pageHeight - y - h`.
 */
export interface Placement {
  id: string;
  fieldKey: string;
  /** 1-based page number */
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  fontSize: number;
  /** Text alignment within the box; missing on placements saved before this existed → 'left' */
  align?: TextAlign;
  /** Font of the printed value, or a barcode (see `FONT_OPTIONS`); missing → Arial / Helvetica */
  font?: PlacementFont;
  /** Drawn on the generated PDF only when all of these are met (see placementConditions.ts); none → always */
  conditions?: PlacementCondition[];
  /** Printed instead of the field's value; missing → the value itself */
  displayAs?: PlacementDisplay;
}

/** The applicant's own identifying / contact info */
export const APPLICATION_MAPPER_FIELDS: MapperField[] = [
  { key: 'applicantName', label: 'Applicant Name', source: 'Application', sampleValue: 'María Gonzalez' },
  {
    key: 'applicantAddress',
    label: 'Applicant Address',
    source: 'Application',
    sampleValue: '1200 W Main St, Dallas, TX 75201',
  },
  { key: 'applicantPhone', label: 'Applicant Phone', source: 'Application', sampleValue: '(214) 555-0147' },
  { key: 'applicantEmail', label: 'Applicant Email', source: 'Application', sampleValue: 'maria.gonzalez@example.com' },
];

/**
 * Default fields, already known by the system, available on every custom PDF.
 *
 * `ipAddress` / `browserType` / `browserDevice` are resolved from the application's own consent
 * record (`getConsentById`, the first row whose `description` is "consent page") — see
 * `resolveApplicationMetaValues`. `acceptedDate` has no backing data yet: there's no confirmed field
 * distinguishing "Accepted" from "Approved" on the application. It's listed here so it can be placed,
 * but resolves to '' until that data exists.
 *
 * `approvalSignature` is the signature of the staff member who approved the assistance request the PDF is
 * generated for (`GET /assistance-requests/{id}/approval-signatures`). It's only collected where the
 * service area's "Approval Signature Configuration" requires it for that assistance type, so placing it
 * warns the admin to set that up (see the PDF mapper).
 */
export const APPROVAL_SIGNATURE_KEY = 'approvalSignature';
/**
 * Funding source(s) of the funds allocated to the assistance request the PDF is generated for. Only
 * financial assistance types get funds, so the mapper only offers it when one of the form's assistance
 * types requires funding (see the PDF mapper).
 */
export const FUNDING_SOURCE_KEY = 'fundingSource';
export const SYSTEM_MAPPER_FIELDS: MapperField[] = [
  // what the PDF is generated for: the application and its assistance request (public ids, as shown
  // across the admin dashboard) and that request's assistance type
  { key: 'applicantId', label: 'Applicant ID', source: 'System', sampleValue: 'SS-10-001-000-123' },
  { key: 'applicationId', label: 'Application ID', source: 'System', sampleValue: 'APP-10001-000411' },
  { key: 'assistanceRequestId', label: 'Assistance Request ID', source: 'System', sampleValue: 'AR-10-001-076-143' },
  { key: 'assistanceType', label: 'Assistance Type', source: 'System', sampleValue: 'Electricity' },
  { key: FUNDING_SOURCE_KEY, label: 'Funding Source', source: 'System', sampleValue: 'EFSP' },
  { key: 'ipAddress', label: 'IP Address', source: 'System', sampleValue: '73.162.10.4' },
  { key: 'browserType', label: 'Browser Type', source: 'System', sampleValue: 'Safari' },
  { key: 'browserDevice', label: 'Browser Device', source: 'System', sampleValue: 'iPhone' },
  { key: 'applicationDate', label: 'Create Date', source: 'System', sampleValue: '09/28/2026' },
  { key: 'submissionDate', label: 'Submission Date', source: 'System', sampleValue: '09/29/2026' },
  { key: 'acceptedDate', label: 'Accepted Date', source: 'System', sampleValue: '09/30/2026' },
  // approval of the assistance request the PDF is generated for (`approvalDate` of GET /assistance-requests/{id})
  { key: 'approvedDate', label: 'Assistance Request Approved Date', source: 'System', sampleValue: '10/01/2026' },
  {
    key: 'generationDate',
    label: 'PDF Generation Date',
    source: 'System',
    sampleValue: new Date().toLocaleDateString('en-US'),
  },
  // Household totals — same figures the placement Conditions are evaluated on (see `aggregateValuesFor`)
  { key: 'totalGrossAnnualIncome', label: 'Total Gross Annual Income', source: 'System', sampleValue: '$25,800.00' },
  { key: 'totalGrossMonthlyIncome', label: 'Total Gross Monthly Income', source: 'System', sampleValue: '$2,150.00' },
  { key: 'totalNetAnnualIncome', label: 'Total Net Annual Income', source: 'System', sampleValue: '$4,200.00' },
  { key: 'totalNetMonthlyIncome', label: 'Total Net Monthly Income', source: 'System', sampleValue: '$350.00' },
  { key: 'totalHouseholdMembers', label: 'Total HH Members', source: 'System', sampleValue: '3' },
  {
    key: APPROVAL_SIGNATURE_KEY,
    label: 'Approval Signature',
    source: 'System',
    kind: 'signature',
    sampleValue: 'John Carter',
  },
];

/** Household Members step: repeated/array-shaped rows, not a single value — not placeable on a PDF */
export const HOUSEHOLD_STEP_ID = '4811a82e-0561-4ad6-9ea5-180a8ffed3d1';

/** `aq:{stepId}:{fieldName}` → the step id and field name it was built from, or null if not one of these */
export const parseApplicationQuestionKey = (key: string): { stepId: string; fieldName: string } | null => {
  if (!key.startsWith('aq:')) return null;
  const [, stepId, fieldName] = key.split(':');
  return stepId && fieldName ? { stepId, fieldName } : null;
};

const sampleFor = (formkit: string, label: string) => {
  switch (formkit) {
    case 'number':
      return '4';
    case 'mask':
      return '$1,250.00';
    case 'date':
      return '10/01/2026';
    case 'file':
      return 'document.pdf';
    case SIGNATURE_TYPE:
      // static fallback only: the real sample is a signature image generated by `buildSampleValues`
      return '';
    default:
      return `Sample: ${label}`;
  }
};

/** A step's own name for the "Custom Questions" step type, gated differently below (see `customQuestionLinkIds`) */
const CUSTOM_QUESTIONS_STEP_NAME = 'Custom Questions';

/**
 * Every other field an applicant can fill in anywhere on the application — the built-in intake form
 * (household, income, contact info, etc.), not this Custom Form's own questions — for any assistance
 * type this Custom Form applies to (or a step with no assistance type restriction).
 *
 * Filtered exactly like `AssistanceRequestSpecification/index.vue` shows them for one assistance type,
 * so the two stay in sync:
 * - `territoryRequiredByLinkId`: `groupByAttribute(getByTerritory().data, 'formFieldFormStepID', 'isRequired')`
 *   — a field/step link only appears here at all once it's configured for the current territory.
 * - `customQuestionLinkIds`: the union, across this form's assistance types, of
 *   `getByAssistanceTypeAsync(atId).data[].formFieldFormStepID` — "Custom Questions" steps additionally
 *   need their own explicit per-assistance-type link (the reference page's own extra gate for that step).
 *
 * File questions and the Household Members step are excluded: a file has nothing printable, and a
 * household row repeats per member. Grouped into one palette section per step (its `source`), in the
 * step's own display order.
 */
export const buildApplicationQuestionFields = (
  form: CustomForm | null | undefined,
  allSteps: FormFieldFormStep[],
  territoryRequiredByLinkId: Record<string, boolean>,
  customQuestionLinkIds: Set<string>
): MapperField[] => {
  const assistanceTypeIds = new Set(form?.assistanceTypeIds ?? []);
  const rows = (allSteps ?? [])
    .filter((r) => r.id in territoryRequiredByLinkId)
    .filter((r) => r.formStep.id !== HOUSEHOLD_STEP_ID)
    .filter((r) => r.formField.formkitSchema?.$formkit !== 'file')
    .filter((r) => r.formStep.assistanceTypeId === null || assistanceTypeIds.has(r.formStep.assistanceTypeId))
    .filter((r) => r.formStep.name !== CUSTOM_QUESTIONS_STEP_NAME || customQuestionLinkIds.has(r.id))
    .sort((a, b) => (a.formStep.displayOrder ?? 0) - (b.formStep.displayOrder ?? 0) || a.displayOrder - b.displayOrder);

  const seenKeys = new Set<string>();
  const fields: MapperField[] = [];
  for (const r of rows) {
    const name = r.formField.formkitSchema?.name;
    if (!name) continue;
    const key = `aq:${r.formStep.id}:${name}`;
    if (seenKeys.has(key)) continue; // the same field can be linked to a step more than once
    seenKeys.add(key);
    const label = r.formField.formkitSchema?.label || name;
    fields.push({
      key,
      label,
      source: r.formStep.description || r.formStep.name || 'Application Questions',
      sampleValue: sampleFor(r.formField.formkitSchema?.$formkit, label),
    });
  }
  return fields;
};

/**
 * Same `aq:{stepId}:{fieldName}` fields as `buildApplicationQuestionFields`, but read off the
 * `formkitForm.steps` the application and its assistance requests already carry — the exact steps and
 * fields the applicant was shown. Used when filling a real application's PDF, where the
 * territory/assistance-type endpoints behind `useApplicationQuestionsData` are both unnecessary (the
 * answers already tell us which fields exist) and unreliable (they are permission-scoped, so a field the
 * caller can't see would silently lose its placement).
 */
export const buildApplicationQuestionFieldsFromSteps = (sources: any[]): MapperField[] => {
  const seenKeys = new Set<string>();
  const fields: MapperField[] = [];
  for (const src of sources) {
    for (const step of src?.formkitForm?.steps ?? []) {
      if (!step?.stepId || step.stepId === HOUSEHOLD_STEP_ID) continue;
      for (const f of step.fields ?? []) {
        if (!f?.name || f.$formkit === 'file') continue;
        const key = `aq:${step.stepId}:${f.name}`;
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        const label = f.label || f.name;
        fields.push({
          key,
          label,
          source: step.description || step.name || 'Application Questions',
          sampleValue: sampleFor(f.$formkit, label),
        });
      }
    }
  }
  return fields;
};

/**
 * The form's questions (Block Text / Empty Line excluded), the applicant's own info, the system
 * defaults, and — when `allSteps` is given (see `useApplicationQuestionsData`) — every other field the
 * applicant can fill in anywhere on the application (`buildApplicationQuestionFields`).
 */
export const buildMapperFields = (
  form?: CustomForm | null,
  allSteps: FormFieldFormStep[] = [],
  territoryRequiredByLinkId: Record<string, boolean> = {},
  customQuestionLinkIds: Set<string> = new Set()
): MapperField[] => {
  // Block Text is placeable (its text is printed as is); an Empty Line has nothing to print
  const presentation: MapperField[] = (form?.fields ?? [])
    .filter((f) => f.active && (!isDisplayOnlyField(f.formkitSchema) || f.formkitSchema?.$formkit === BLOCK_TEXT_TYPE))
    .sort((a, b) => effectiveDisplayOrder(a) - effectiveDisplayOrder(b))
    .map((f) =>
      f.formkitSchema?.$formkit === BLOCK_TEXT_TYPE
        ? {
            key: f.formkitSchema?.name || f.name || f.id,
            label: `Block Text: ${formElementLabel(f)}`,
            source: 'Presentation',
            kind: 'text',
            multiline: true,
            sampleValue: blockTextContent(f.formkitSchema?.helpLong),
          }
        : isCheckboxField(f.formkitSchema)
        ? {
            // placed as the check mark itself, sized like one (see the mapper's `defaultBox`)
            key: f.formkitSchema?.name || f.name || f.id,
            label: f.formkitSchema?.label || f.name,
            source: 'Presentation',
            kind: 'symbol',
            symbol: 'checkmark',
            whenAnswered: true,
            sampleValue: '✓',
          }
        : {
            key: f.formkitSchema?.name || f.name || f.id,
            label: f.formkitSchema?.label || f.name,
            source: 'Presentation',
            kind: isSignatureField(f.formkitSchema) ? 'signature' : 'text',
            sampleValue: sampleFor(f.formkitSchema?.$formkit, f.formkitSchema?.label || f.name),
          }
    );
  return [
    ...SYMBOL_MAPPER_FIELDS,
    ...presentation,
    ...APPLICATION_MAPPER_FIELDS,
    ...SYSTEM_MAPPER_FIELDS,
    ...buildApplicationQuestionFields(form, allSteps, territoryRequiredByLinkId, customQuestionLinkIds),
  ];
};

export const sampleValuesFor = (fields: MapperField[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.key, f.sampleValue]));

const SAMPLE_SIGNATURE_INK = '#0d2680';
const SAMPLE_SIGNATURE_FONT =
  '"Brush Script MT", "Segoe Script", "Snell Roundhand", "Apple Chancery", "URW Chancery L", cursive';
const sampleSignatureCache = new Map<string, string>();

/**
 * Hand-written looking sample signature (PNG data URL) for "Preview with sample data": the name in
 * a cursive font over a slightly wavy underline, drawn on a transparent 420×140 canvas.
 * Browser only — returns '' when there is no DOM or the canvas cannot be drawn. Cached per name.
 */
export const sampleSignatureDataUrl = async (name: string): Promise<string> => {
  if (typeof document === 'undefined') return '';
  const cached = sampleSignatureCache.get(name);
  if (cached !== undefined) return cached;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 420;
    canvas.height = 140;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';

    // underline: a shallow, slightly irregular stroke like a pen flourish
    ctx.strokeStyle = SAMPLE_SIGNATURE_INK;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(14, 106);
    ctx.bezierCurveTo(110, 96, 210, 118, 300, 104);
    ctx.bezierCurveTo(340, 98, 380, 102, 404, 108);
    ctx.stroke();

    // name in a script font, shrunk until it fits the canvas width
    const fontFor = (size: number) => `italic 600 ${size}px ${SAMPLE_SIGNATURE_FONT}`;
    let size = 58;
    ctx.font = fontFor(size);
    while (size > 16 && ctx.measureText(name).width > 390) {
      size -= 2;
      ctx.font = fontFor(size);
    }
    ctx.fillStyle = SAMPLE_SIGNATURE_INK;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(name, 16, 88);

    const url = canvas.toDataURL('image/png');
    sampleSignatureCache.set(name, url);
    return url;
  } catch (e) {
    console.warn('unable to draw the sample signature', e);
    return '';
  }
};

/**
 * Sample values used to preview a template: faker data for the default applicant fields and
 * type-appropriate values for the form's questions. Falls back to the static `sampleValue`.
 * Signature questions get a generated signature image of the sample applicant.
 */
export const buildSampleValues = async (
  fields: MapperField[],
  form?: CustomForm | null
): Promise<Record<string, string>> => {
  const values = sampleValuesFor(fields);
  // always the actual date, not the (possibly stale) module-load-time default
  if ('generationDate' in values) values.generationDate = new Date().toLocaleDateString('en-US');
  try {
    const { faker } = await import('@faker-js/faker');
    const first = faker.name.firstName();
    const last = faker.name.lastName();
    values.applicantName = `${first} ${last}`;
    values.applicantAddress = `${faker.address.streetAddress()}, ${faker.address.city()}, ${faker.address.stateAbbr()} ${faker.address.zipCode(
      '#####'
    )}`;
    values.applicantPhone = faker.phone.number('(###) ###-####');
    values.applicantEmail = faker.internet.email(first, last).toLowerCase();
    values.applicantDateOfBirth = faker.date.birthdate({ min: 18, max: 80, mode: 'age' }).toLocaleDateString('en-US');
    (form?.fields ?? []).forEach((f) => {
      const key = f.formkitSchema?.name || f.name || f.id;
      if (!(key in values)) return;
      switch (f.formkitSchema?.$formkit) {
        case 'number':
          values[key] = `${faker.datatype.number({ min: 1, max: 9 })}`;
          break;
        case 'mask':
          values[key] = `$${faker.finance.amount(50, 2500, 2)}`;
          break;
        case 'date':
          values[key] = faker.date.soon(60).toLocaleDateString('en-US');
          break;
        case 'select':
          values[key] = f.formkitSchema?.options?.length
            ? faker.helpers.arrayElement(f.formkitSchema.options).label
            : faker.word.adjective();
          break;
        case 'file':
          values[key] = `${faker.system.commonFileName('pdf')}`;
          break;
        case 'textarea':
          values[key] = faker.lorem.sentence(8);
          break;
        case CHECKBOX_TYPE:
          // ticked, so the preview shows the check mark
          values[key] = CHECKBOX_ON_VALUE;
          break;
        case SIGNATURE_TYPE:
          // generated below, once the sample applicant name is known
          break;
        case BLOCK_TEXT_TYPE:
          // its own text, not sample data
          break;
        default:
          values[key] = faker.lorem.words(3);
      }
    });
  } catch (e) {
    console.warn('faker unavailable, using static sample values', e);
  }

  // the approver is a staff member, not the applicant: a different sample name
  if (APPROVAL_SIGNATURE_KEY in values) {
    const approver = fields.find((f) => f.key === APPROVAL_SIGNATURE_KEY)?.sampleValue || 'John Carter';
    values[APPROVAL_SIGNATURE_KEY] = (await sampleSignatureDataUrl(approver)) || approver;
  }

  const signer = values.applicantName || 'María Gonzalez';
  for (const f of form?.fields ?? []) {
    const key = f.formkitSchema?.name || f.name || f.id;
    if (!isSignatureField(f.formkitSchema) || !(key in values)) continue;
    // the image, or the signer's name (drawn in a script font) when the canvas is unavailable
    values[key] = (await sampleSignatureDataUrl(signer)) || signer;
  }
  return values;
};
